import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Add, Edit, Delete, Search, Clear } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

const STATUS_COLORS: Record<string, 'warning' | 'success' | 'default'> = {
  draft: 'warning',
  published: 'success',
  archived: 'default',
};

const PAGE_SIZE = 9;

function resolveImageUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  const apiBase = API_BASE_URL.replace('/api/v1', '');
  return apiBase + url;
}

// Collapsible tag list — shows max 3, click "more" to expand
const TagList: React.FC<{ tags: string[] }> = ({ tags }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? tags : tags.slice(0, 3);
  const hasMore = tags.length > 3;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
      {visible.map((t: string) => (
        <Chip key={t} label={`#${t}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
      ))}
      {hasMore && (
        <Chip
          label={expanded ? 'show less' : `+${tags.length - 3} more`}
          size="small"
          variant="outlined"
          onClick={() => setExpanded(!expanded)}
          sx={{ fontSize: '0.7rem', cursor: 'pointer', color: 'primary.main', borderColor: 'primary.main' }}
        />
      )}
    </Box>
  );
};

const API_BASE_URL = import.meta.env.DEV
  ? `http://localhost:3000/api/v1`
  : 'https://back.famalink.app/api/v1';

const Articles: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  useEffect(() => {
    apiService.getArticleTags().then((res: any) => setTags(res.data.tags || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchArticles(), 300);
    return () => clearTimeout(t);
  }, [statusFilter, tagFilter, search, page]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params: any = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (tagFilter !== 'all') params.tag = tagFilter;
      if (search.trim()) params.search = search.trim();
      const res: any = await apiService.getArticles(params);
      setArticles(res.data.articles || []);
      setTotal(res.data.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiService.deleteArticle(deleteTarget.id);
      setArticles((prev) => prev.filter((a) => a.id !== deleteTarget.id));
      setDeleteTarget(null);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasActiveFilters = statusFilter !== 'all' || tagFilter !== 'all' || search.trim() !== '';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Articles</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/articles/create')}
        >
          New Article
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Filter bar */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Tag</InputLabel>
            <Select
              value={tagFilter}
              label="Tag"
              onChange={(e) => setTagFilter(e.target.value)}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
              <MenuItem value="all">All</MenuItem>
              {tags.map((t) => (
                <MenuItem key={t.id} value={t.slug}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search title or summary…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          {hasActiveFilters && (
            <Button size="small" startIcon={<Clear />} onClick={() => { setStatusFilter('all'); setTagFilter('all'); setSearch(''); setPage(1); }}>
              Clear
            </Button>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {loading ? 'Loading…' : `${total} article${total !== 1 ? 's' : ''} found`}
        </Typography>
      </Card>

      {/* Card grid */}
      {loading && articles.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : articles.length === 0 ? (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">No articles match these filters.</Typography>
          {hasActiveFilters && (
            <Button size="small" sx={{ mt: 1 }} onClick={() => { setStatusFilter('all'); setTagFilter('all'); setSearch(''); }}>
              Clear filters
            </Button>
          )}
        </Card>
      ) : (
        <>
          <Grid container spacing={3}>
            {articles.map((a) => {
              const catLabel = a.category_label || a.category;
              const catColor = a.category_color || '#666';
              return (
                <Grid item xs={12} sm={6} md={4} key={a.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'box-shadow .2s', '&:hover': { boxShadow: 4 } }}>
                    {/* Cover image */}
                    <Box sx={{ position: 'relative', pt: '56.25%', overflow: 'hidden' }}>
                      {a.image_url ? (
                        <CardMedia
                          component="img"
                          image={resolveImageUrl(a.image_url)}
                          alt={a.image_alt || a.title}
                          sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="caption" color="text.secondary">No image</Typography>
                        </Box>
                      )}
                      <Chip
                        label={a.status}
                        size="small"
                        color={STATUS_COLORS[a.status] || 'default'}
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      />
                    </Box>

                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Chip
                        label={catLabel}
                        size="small"
                        sx={{ alignSelf: 'flex-start', mb: 1, bgcolor: catColor + '20', color: catColor, fontWeight: 600 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{ mb: 1, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                        onClick={() => navigate(`/articles/edit/${a.id}`)}
                      >
                        {a.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, flexGrow: 1 }}>
                        {a.summary || 'No summary'}
                      </Typography>

                      {a.tags && a.tags.length > 0 && (
                        <TagList tags={a.tags} />
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #eee' }}>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(a.updated_at).toLocaleDateString()}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton size="small" onClick={() => navigate(`/articles/edit/${a.id}`)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(a)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Article</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete this {deleteTarget?.status} article?
            <br />
            <strong>"{deleteTarget?.title}"</strong>
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Articles;
